import { useCallback, useContext, useState } from 'react'
import ExitIntentModal from './ExitIntentModal'
import useExitIntent from 'hooks/useExitIntent'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import { trackClientEvent } from 'analytics/analytics'
import ModalContext from 'contexts/ModalContext'

interface Props {
  offerId: string
  propertyName?: string
}

function ExitIntentModalContainer(props: Props) {
  const { offerId, propertyName } = props
  const showModal = useContext(ModalContext)
  const [hasReachedInactivityThreshold, setHasReachedInactivityThreshold] = useState(false)

  const experiment = useAppSelector(state =>
    state.optimizely.optimizelyExperiments[OptimizelyExperiments.croCaptureBouncingCustomersWithYamlModalV2],
  )
  const normalizedVariationKey = experiment?.variationKey?.toLowerCase()
  const isExperimentParticipating = !!normalizedVariationKey && normalizedVariationKey !== 'off'
  const isTreatmentVariation = getOptimizelyEnabledDecision(normalizedVariationKey)

  // Defer experiment activation until inactivity threshold is reached.
  useOptimizelyExperiment(
    OptimizelyExperiments.croCaptureBouncingCustomersWithYamlModalV2,
    hasReachedInactivityThreshold,
  )

  const handleExitIntent = useCallback(() => {
    setHasReachedInactivityThreshold(true)

    // Only show modal for treatment users, but still trigger analytics for control.
    if (!isTreatmentVariation) {
      return
    }

    // Track when modal is shown
    trackClientEvent({
      subject: 'exit-intent-modal-shown',
      action: 'view',
      category: 'exit-intent-modal',
      type: 'interaction',
      optimizelyEventId: 'exit_intent_modal_impression',
      optimizelyEventKey: 'exit_intent_modal_shown',
    })

    showModal(<ExitIntentModal offerId={offerId} propertyName={propertyName} />).then(() => {
      // Track when modal is closed
      trackClientEvent({
        subject: 'exit-intent-modal-closed',
        action: 'close',
        category: 'exit-intent-modal',
        type: 'interaction',
        optimizelyEventId: 'exit_intent_modal_close',
        optimizelyEventKey: 'exit_intent_modal_closed',
      })
    })
  }, [showModal, offerId, propertyName, isTreatmentVariation])

  useExitIntent({
    enabled: isExperimentParticipating,
    onExitIntent: handleExitIntent,
  })

  return null
}

export default ExitIntentModalContainer
